"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import {
  FaChrome,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaLightbulb,
  FaArrowRight,
} from "@/components/icons";
import {
  HiSwitchHorizontal,
  HiScale,
  HiQuestionMarkCircle,
  HiBookOpen,
  HiStar,
  HiLightningBolt,
} from "@/components/icons";
import { SiGamebanana } from "@/components/icons";
import InstallButton from "@/components/InstallButton";
import DynamicIcon from "@/components/product/DynamicIcon";
import { generateBreadcrumbs } from "@/components/BreadCrumb";
import HeaderShell from "@/components/header/HeaderShell";
import ShimmerEffect from "@/components/ShimmerEffect";
import { getProductIdFromPathname, getAllProducts, type ProductId } from "@/lib/products";

import { PRICING } from "@/lib/pricing-constants";
import { BUNDLE_FULL_VALUE } from "@/lib/product-stats";

export default function Header() {
  const pathname = usePathname();
  const [actualPathname, setActualPathname] = useState(pathname);
  // Normalize pathname to always have trailing slash (except for root)
  const normalizedPathname = actualPathname === "/" ? "/" : actualPathname.replace(/\/$/, "") + "/";
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const [isPricingMenuOpen, setIsPricingMenuOpen] = useState(false);
  const [isResourcesMenuOpen, setIsResourcesMenuOpen] = useState(false);

  // Sync actualPathname with pathname from usePathname() (catches Next.js Link navigation)
  // This ensures Header updates when navigating via Next.js router
  useEffect(() => {
    setActualPathname(pathname);
  }, [pathname]);

  // Use a separate state for client-only rendering to avoid hydration mismatch
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Also sync with window.location to catch manual URL updates (e.g., from pricing page)
  // This ensures Header updates when pricing page uses window.history.replaceState
  useEffect(() => {
    // Update from window.location to catch manual URL changes
    const updatePathname = () => {
      if (typeof window !== "undefined") {
        const windowPath = window.location.pathname;
        setActualPathname((current) => {
          // Only update if actually different to avoid unnecessary re-renders
          return windowPath !== current ? windowPath : current;
        });
      }
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener("popstate", updatePathname);
    // Listen for custom event that pricing page dispatches when it updates URL
    window.addEventListener("urlchange", updatePathname);

    return () => {
      window.removeEventListener("popstate", updatePathname);
      window.removeEventListener("urlchange", updatePathname);
    };
  }, []); // Empty deps - only set up listeners once

  // Generate breadcrumbs for current page
  const breadcrumbs = generateBreadcrumbs(actualPathname);
  const isHomePage = actualPathname === "/";

  // Close mobile menu on Escape key
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileMenuOpen]);

  // Determine which product to highlight in pricing based on current page
  const getPricingUrl = () => {
    // Only run on client to avoid hydration mismatch, or use default on server
    if (!mounted) return "/products/";

    const productId = getProductIdFromPathname(actualPathname);

    // If on a product page or install/uninstall page, go to product-specific
    // pricing page — but only if that product actually has a pricing page.
    // Free-only products (no `offers`) skip /pricing/<slug>/ generation, so
    // the header "Get Pro" CTA falls back to the /pricing/ index for them.
    if (
      productId &&
      (normalizedPathname.startsWith("/product/") ||
        normalizedPathname.startsWith("/install/") ||
        normalizedPathname.startsWith("/uninstall/"))
    ) {
      // Find product slug for clean URL
      const allProducts = getAllProducts();
      const product = allProducts.find((p) => p.id === productId);
      if (product?.offers) {
        return `/pricing/${product.slug}/#pricing-cards`;
      }
    }

    // For all other pages (including /products/ and free-only product pages),
    // go to the pricing landing page.
    return "/pricing/";
  };

  // Determine which product to install based on current page
  const getInstallProduct = (): ProductId => {
    // Only run on client to avoid hydration mismatch, or use default on server
    if (!mounted) return "x-unfollow-pro";

    const productId = getProductIdFromPathname(actualPathname);
    return productId || "x-unfollow-pro"; // Default fallback
  };

  // Get all products from source of truth
  const allProducts = getAllProducts();

  // Sort products by headerOrder (descending) for dropdown ordering
  const sortedProducts = [...allProducts].sort((a, b) => b.headerOrder - a.headerOrder);

  // Main navigation items (Desktop)
  const navItems = [
    { href: getPricingUrl(), label: "Pricing" },
    { href: "/support/", label: "Support", desktopClassName: "hidden lg:inline-flex" },
  ];

  // Action buttons for desktop
  const getPrimaryActionButton = () => {
    // Only run on client to avoid hydration mismatch, or use default on server
    if (!mounted) {
      return {
        component: Link,
        props: {
          href: "/products/",
          className:
            "hidden md:inline-flex h-9 items-center justify-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:shadow-primary/25 gap-2",
        },
        mobileClassName:
          "flex h-9 items-center justify-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all gap-2",
        children: (
          <>
            <FaChrome className="h-4 w-4" />
            Install Free
          </>
        ),
      };
    }

    // On product/install/uninstall pages, show Install Free button with product context
    const productId = getProductIdFromPathname(actualPathname);
    if (productId || normalizedPathname.startsWith("/product/")) {
      return {
        component: InstallButton,
        props: {
          source: "header",
          className:
            "hidden md:inline-flex h-9 items-center justify-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:shadow-primary/25 gap-2",
        },
        mobileClassName:
          "flex h-9 items-center justify-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all gap-2",
        children: "Install Free",
      };
    }

    // On products list page, scroll to list instead of self-linking
    if (normalizedPathname === "/products/") {
      return {
        component: Link,
        props: {
          href: "#products-list",
          className:
            "hidden md:inline-flex h-9 items-center justify-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:shadow-primary/25 gap-2",
        },
        mobileClassName:
          "flex h-9 items-center justify-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all gap-2",
        children: (
          <>
            <FaChrome className="h-4 w-4" />
            Install Free
          </>
        ),
      };
    }

    // Default for other pages: Go to products list
    return {
      component: Link,
      props: {
        href: "/products/",
        className:
          "hidden md:inline-flex h-9 items-center justify-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:shadow-primary/25 gap-2",
      },
      mobileClassName:
        "flex h-9 items-center justify-center rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground shadow-lg hover:bg-primary/90 transition-all gap-2",
      children: (
        <>
          <FaChrome className="h-4 w-4" />
          Install Free
        </>
      ),
    };
  };

  const actionButtons = [
    getPrimaryActionButton(),
    {
      component: Link,
      props: {
        href: getPricingUrl(),
        className:
          "group relative overflow-hidden hidden md:inline-flex h-9 items-center justify-center rounded-full border border-white/10 bg-black px-6 text-sm font-bold text-white shadow-lg hover:bg-black/90 transition-all",
      },
      mobileClassName:
        "flex h-9 items-center justify-center rounded-full bg-black px-6 text-sm font-bold text-white shadow-lg hover:bg-black/90 transition-all",
      children: "Get Pro",
    },
  ];
  return (
    <>
      <HeaderShell
        breadcrumbs={breadcrumbs}
        isHomePage={isHomePage}
        hideBreadcrumb={isDesktopMenuOpen || isPricingMenuOpen || isResourcesMenuOpen}
      >
        {/* Logo */}
        <div className="flex items-center gap-3">
          <Link
            href="/"
            className="flex items-center gap-2 group transition-transform hover:scale-105"
          >
            <div className="relative">
              <SiGamebanana
                className="h-8 w-8 text-primary transition-all duration-700 group-hover:animate-bounce group-hover:rotate-12"
                style={{
                  animation: "none",
                  transformOrigin: "center",
                }}
                aria-hidden="true"
              />
              {/* Subtle glow effect */}
              <div className="absolute inset-0 bg-primary/10 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <span className="text-xl font-bold tracking-tight text-primary group-hover:text-primary/90 transition-all duration-500">
              <span className="bg-gradient-to-r from-primary via-primary/90 to-primary/80 bg-clip-text text-transparent group-hover:from-primary/90 group-hover:via-primary group-hover:to-primary/90">
                PlugMonkey
              </span>
            </span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-2">
          {/* Products Dropdown */}
          <div
            className="relative group"
            onMouseEnter={() => setIsDesktopMenuOpen(true)}
            onMouseLeave={() => setIsDesktopMenuOpen(false)}
          >
            <Link
              href="/products/"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
                normalizedPathname.startsWith("/product/") || normalizedPathname === "/products/"
                  ? "text-primary"
                  : "text-muted-foreground"
              }`}
              onClick={() => setIsDesktopMenuOpen(false)}
              aria-haspopup="true"
              aria-expanded={isDesktopMenuOpen}
            >
              Products
              <FaChevronDown
                className={`h-3 w-3 transition-transform duration-300 ${
                  isDesktopMenuOpen ? "-rotate-180" : ""
                }`}
              />
            </Link>

            {/* Dropdown Menu */}
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[800px] z-10 animate-in fade-in slide-in-from-top-2 duration-300 ${
                isDesktopMenuOpen ? "block" : "hidden"
              }`}
              onMouseLeave={() => {
                const activeElement = document.activeElement;
                if (activeElement instanceof HTMLElement) {
                  activeElement.blur();
                }
              }}
            >
              <div className="rounded-[2rem] border border-border/60 bg-popover p-3 grid grid-cols-2 gap-1.5 shadow-[0_12px_36px_rgba(0,0,0,0.12)] dark:shadow-[0_12px_36px_rgba(0,0,0,0.5)] max-h-[calc(100vh-7rem)] overflow-y-auto">
                <div className="col-span-2 pb-2 border-b border-border/10 text-center -mt-3 -mx-3 pt-3">
                  <Link
                    href="/products/"
                    className="inline-flex items-center gap-2 text-xs font-bold text-primary hover:text-primary/80 uppercase tracking-widest py-1.5 px-6 rounded-full hover:bg-primary/5 transition-colors"
                    onClick={() => setIsDesktopMenuOpen(false)}
                  >
                    View All Chrome Extensions <FaChevronDown className="-rotate-90 w-3 h-3" />
                  </Link>
                </div>
                {sortedProducts.map((product) => (
                  <Link
                    key={product.id}
                    href={`/product/${product.slug}/`}
                    className="flex items-start gap-3 p-3 rounded-2xl border border-gray-100 dark:border-white/5 hover:border-primary/30 hover:bg-gray-50 hover:shadow-md hover:shadow-black/[0.03] transition-all duration-200 group/item"
                    onClick={() => setIsDesktopMenuOpen(false)}
                  >
                    <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center group-hover/item:shadow-md group-hover/item:shadow-primary/10 transition-all duration-200">
                      <DynamicIcon
                        name={product.icon.name || "HiCube"}
                        className="w-5 h-5 text-primary"
                        useDefaultColors
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-gray-900 group-hover/item:text-primary transition-colors flex items-center gap-1.5 leading-tight">
                        {product.shortName}
                        <FaArrowRight className="w-2.5 h-2.5 opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all" />
                      </div>
                      <div className="text-xs text-gray-500 line-clamp-1 mt-0.5 leading-snug group-hover/item:text-gray-600 transition-colors">
                        {product.description}
                      </div>
                    </div>
                  </Link>
                ))}

                {/* Bundle Upsell — slim variant (parity with Pricing dropdown, but compacted for the taller Products card grid) */}
                <Link
                  href="/bundle/"
                  className="col-span-2 relative rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 px-5 py-3 flex items-center gap-4 overflow-hidden group/bundle hover:shadow-xl transition-all duration-300"
                  onClick={() => setIsDesktopMenuOpen(false)}
                >
                  <div className="absolute inset-0 bg-grid-pattern opacity-[0.05]" />
                  <div className="absolute top-0 right-0 w-40 h-40 bg-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 group-hover/bundle:bg-primary/30 transition-colors" />

                  <div className="relative z-10 flex items-center gap-2 shrink-0">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/10 text-white text-[10px] font-bold uppercase tracking-wider border border-white/10">
                      <SiGamebanana className="w-3 h-3 text-primary" aria-hidden="true" />
                      Best Value
                    </div>
                  </div>

                  <div className="relative z-10 flex-1 min-w-0">
                    <p className="text-sm font-bold text-white leading-tight">
                      All-Access <span className="text-primary">Bundle</span>
                      <span className="text-slate-400 font-normal">
                        {" "}
                        — every extension, forever
                      </span>
                    </p>
                  </div>

                  <div className="relative z-10 flex items-center gap-3 shrink-0">
                    <div className="flex items-baseline gap-1.5 text-white">
                      <span className="text-lg font-bold">${PRICING.BUNDLE_LIFETIME}</span>
                      <span className="text-[10px] text-slate-400 line-through">
                        ${BUNDLE_FULL_VALUE}
                      </span>
                    </div>
                    <div className="h-8 px-4 rounded-full bg-white text-slate-900 font-bold text-xs flex items-center justify-center gap-1.5 group-hover/bundle:scale-[1.02] transition-transform shadow-lg">
                      Get Bundle <FaArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>

                <div className="col-span-2 pt-2 border-t border-border/10 text-center pb-3 -mb-3 -mx-3">
                  <Link
                    href="/support/?subject=New Tool or Feature Request"
                    className="inline-flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-primary uppercase tracking-widest py-1.5 px-4 rounded-full hover:bg-gray-50 transition-colors"
                    onClick={() => setIsDesktopMenuOpen(false)}
                  >
                    <FaLightbulb className="w-3 h-3" />
                    Suggest a Product
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Dropdown */}
          <div
            className="relative group"
            onMouseEnter={() => setIsPricingMenuOpen(true)}
            onMouseLeave={() => setIsPricingMenuOpen(false)}
          >
            <Link
              href={getPricingUrl()}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
                normalizedPathname.startsWith("/pricing/")
                  ? "text-primary"
                  : "text-muted-foreground"
              }`}
              onClick={() => setIsPricingMenuOpen(false)}
              aria-haspopup="true"
              aria-expanded={isPricingMenuOpen}
            >
              Pricing
              <FaChevronDown
                className={`h-3 w-3 transition-transform duration-300 ${
                  isPricingMenuOpen ? "-rotate-180" : ""
                }`}
              />
            </Link>

            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[800px] z-10 animate-in fade-in slide-in-from-top-2 duration-300 ${
                isPricingMenuOpen ? "block" : "hidden"
              }`}
              onMouseLeave={() => {
                const activeElement = document.activeElement;
                if (activeElement instanceof HTMLElement) {
                  activeElement.blur();
                }
              }}
            >
              <div className="rounded-[2rem] border border-border/60 bg-popover p-3 shadow-[0_12px_36px_rgba(0,0,0,0.12)] dark:shadow-[0_12px_36px_rgba(0,0,0,0.5)] max-h-[calc(100vh-7rem)] overflow-y-auto">
                {/* Header label — parity with Products dropdown */}
                <div className="mb-1.5 pb-2 border-b border-border/10 text-center -mt-3 -mx-3 pt-3">
                  <Link
                    href="/pricing/"
                    className="inline-flex items-center gap-2 text-xs font-bold text-primary hover:text-primary/80 uppercase tracking-widest py-1.5 px-6 rounded-full hover:bg-primary/5 transition-colors"
                    onClick={() => setIsPricingMenuOpen(false)}
                  >
                    Compare All Plans <FaChevronDown className="-rotate-90 w-3 h-3" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {sortedProducts
                    .filter((product) => product.offers)
                    .map((product) => (
                      <Link
                        key={product.id}
                        href={`/pricing/${product.slug}/#pricing-cards`}
                        className="flex items-center gap-3 p-3 rounded-2xl border border-gray-100 dark:border-white/5 hover:border-primary/30 hover:bg-gray-50 hover:shadow-md hover:shadow-black/[0.03] transition-all duration-200 group/item"
                        onClick={() => setIsPricingMenuOpen(false)}
                      >
                        <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center group-hover/item:shadow-md group-hover/item:shadow-primary/10 transition-all duration-200">
                          <DynamicIcon
                            name={product.icon.name || "HiCube"}
                            className="w-5 h-5 text-primary"
                            useDefaultColors
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-gray-900 group-hover/item:text-primary transition-colors mb-0.5 leading-tight">
                            {product.shortName}
                          </div>
                          <div className="text-xs font-medium text-gray-400 group-hover/item:text-gray-500 transition-colors leading-snug">
                            Monthly • Yearly • Lifetime
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>

                {/* Bundle Upsell — full-width bottom banner */}
                <Link
                  href="/bundle/"
                  className="relative mt-1.5 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-4 flex items-center gap-6 overflow-hidden group/bundle hover:shadow-xl transition-all duration-300"
                  onClick={() => setIsPricingMenuOpen(false)}
                >
                  {/* Decorative Background */}
                  <div className="absolute inset-0 bg-grid-pattern opacity-[0.05]" />
                  <div className="absolute top-0 right-0 w-48 h-48 bg-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 group-hover/bundle:bg-primary/30 transition-colors" />

                  <div className="relative z-10 flex items-center gap-2 shrink-0">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-white text-[10px] font-bold uppercase tracking-wider border border-white/10">
                      <SiGamebanana className="w-3 h-3 text-primary" aria-hidden="true" />
                      Best Value
                    </div>
                  </div>

                  <div className="relative z-10 flex-1 min-w-0">
                    <p className="text-base font-bold text-white leading-tight">
                      All-Access <span className="text-primary">Bundle</span>
                    </p>
                    <p className="text-sm text-slate-400 leading-snug">
                      Every extension, forever. One price.
                    </p>
                  </div>

                  <div className="relative z-10 flex items-center gap-4 shrink-0">
                    <div className="flex items-baseline gap-1.5 text-white">
                      <span className="text-2xl font-bold">${PRICING.BUNDLE_LIFETIME}</span>
                      <span className="text-xs text-slate-400 line-through">
                        ${BUNDLE_FULL_VALUE}
                      </span>
                    </div>
                    <div className="h-10 px-5 rounded-full bg-white text-slate-900 font-bold text-sm flex items-center justify-center gap-2 group-hover/bundle:scale-[1.02] transition-transform shadow-lg">
                      Get Bundle <FaArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>

          {/* Resources Dropdown */}
          <div
            className="relative group"
            onMouseEnter={() => setIsResourcesMenuOpen(true)}
            onMouseLeave={() => setIsResourcesMenuOpen(false)}
          >
            <Link
              href="/resources/"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors hover:text-primary ${
                normalizedPathname.startsWith("/resources/") ||
                normalizedPathname.startsWith("/blog/") ||
                normalizedPathname.startsWith("/alternatives/") ||
                normalizedPathname.startsWith("/alternative/") ||
                normalizedPathname.startsWith("/comparisons/") ||
                normalizedPathname.startsWith("/comparison/") ||
                normalizedPathname.startsWith("/best/") ||
                normalizedPathname.startsWith("/tools/") ||
                normalizedPathname.startsWith("/tool/") ||
                normalizedPathname.startsWith("/prompts/") ||
                normalizedPathname.startsWith("/prompt/") ||
                // /glossary/ removed from active-link check 2026-06-29 — noindex'd surface
                // /integration/ removed from active-link check 2026-10-20 — surface retired
                normalizedPathname.startsWith("/downloads/") ||
                normalizedPathname.startsWith("/download/")
                  ? "text-primary"
                  : "text-muted-foreground"
              }`}
              aria-haspopup="true"
              aria-expanded={isResourcesMenuOpen}
            >
              Resources
              <FaChevronDown
                className={`h-3 w-3 transition-transform duration-300 ${
                  isResourcesMenuOpen ? "-rotate-180" : ""
                }`}
              />
            </Link>

            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[400px] z-10 animate-in fade-in slide-in-from-top-2 duration-300 ${
                isResourcesMenuOpen ? "block" : "hidden"
              }`}
            >
              <div className="rounded-[2rem] border border-border/60 bg-popover p-4 grid grid-cols-1 gap-1 shadow-[0_12px_36px_rgba(0,0,0,0.12)] dark:shadow-[0_12px_36px_rgba(0,0,0,0.5)]">
                {[
                  // Order mirrors /resources/ hub page sections.
                  // Expanded 2026-06-30 from 3 → 6 items after /for/ removal
                  // left the dropdown thin; matches hub for discoverability.
                  {
                    href: "/blog/",
                    icon: <HiBookOpen className="w-6 h-6" />,
                    label: "Blog",
                    desc: "In-depth guides and product updates",
                  },
                  {
                    href: "/alternatives/",
                    icon: <HiSwitchHorizontal className="w-6 h-6" />,
                    label: "Alternatives",
                    desc: "See how we compare to competitors",
                  },
                  {
                    href: "/comparisons/",
                    icon: <HiScale className="w-6 h-6" />,
                    label: "Comparisons",
                    desc: "Head-to-head feature breakdowns",
                  },
                  {
                    href: "/best/",
                    icon: <HiStar className="w-6 h-6" />,
                    label: "Best Tools",
                    desc: "Curated category roundups",
                  },
                  {
                    href: "/tools/",
                    icon: <HiLightningBolt className="w-6 h-6" />,
                    label: "Free Tools",
                    desc: "Browser tools, no install needed",
                  },
                  {
                    href: "/faq/",
                    icon: <HiQuestionMarkCircle className="w-6 h-6" />,
                    label: "FAQ",
                    desc: "Common questions answered",
                  },
                ].map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className="flex items-center gap-4 p-4 rounded-3xl hover:bg-gray-50 hover:shadow-lg hover:shadow-black/[0.03] transition-all duration-300 group/item border border-transparent hover:border-black/5"
                    onClick={() => setIsResourcesMenuOpen(false)}
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center text-primary group-hover/item:scale-110 group-hover/item:shadow-md group-hover/item:shadow-primary/10 transition-all duration-300">
                      {item.icon}
                    </div>
                    <div className="min-w-0">
                      <div className="text-base font-bold text-gray-900 group-hover/item:text-primary transition-colors">
                        {item.label}
                      </div>
                      <div className="text-sm text-gray-500 group-hover/item:text-gray-600 transition-colors">
                        {item.desc}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {navItems
            .filter((item) => item.label !== "Pricing")
            .map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`px-4 py-2 text-sm font-medium transition-all hover:text-primary hover:scale-105 ${
                  item.desktopClassName || ""
                } ${normalizedPathname === item.href ? "text-primary" : "text-muted-foreground"}`}
              >
                {item.label}
              </Link>
            ))}
        </nav>

        {/* Desktop Action Buttons */}
        <div className="hidden md:flex items-center gap-3">
          {actionButtons.map((button, index) => {
            const Component = button.component;
            // Index 0 = Install Free (primary, no shimmer). Index 1 = Get Pro (gets shimmer).
            const withShimmer = index === 1;
            if (Component === InstallButton) {
              const product = getInstallProduct();
              // Spread props and explicitly set product (product will override any existing product prop)
              const buttonProps = button.props as Record<string, unknown>;
              return (
                <InstallButton key={index} {...buttonProps} product={product}>
                  {button.children}
                </InstallButton>
              );
            }
            return (
              <Link key={index} {...(button.props as React.ComponentProps<typeof Link>)}>
                {withShimmer ? (
                  <>
                    <ShimmerEffect shimmerColor="#ffffff" />
                    <span className="relative z-10 inline-flex items-center gap-2">
                      {button.children}
                    </span>
                  </>
                ) : (
                  button.children
                )}
              </Link>
            );
          })}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden p-2 rounded-md hover:bg-muted transition-colors"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <FaTimes className="h-5 w-5" /> : <FaBars className="h-5 w-5" />}
        </button>
      </HeaderShell>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed top-[80px] left-4 right-4 max-h-[80dvh] overflow-y-auto overscroll-contain touch-pan-y rounded-3xl border border-border/60 bg-background shadow-[0_12px_36px_rgba(0,0,0,0.14)] dark:shadow-[0_12px_36px_rgba(0,0,0,0.55)] md:hidden z-50">
          <div className="container pt-4 pb-[max(2rem,env(safe-area-inset-bottom))] space-y-4">
            {/* Mobile Navigation */}
            <nav className="space-y-1">
              <div className="px-3 py-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                  Products
                </p>
                <div className="space-y-1">
                  {sortedProducts.map((product) => (
                    <Link
                      key={product.id}
                      href={`/product/${product.slug}/`}
                      className={`flex items-center gap-3 py-2 px-3 rounded-md text-sm font-medium transition-colors hover:bg-muted ${
                        normalizedPathname === `/product/${product.slug}/`
                          ? "text-primary bg-primary/10"
                          : "text-foreground"
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <DynamicIcon
                        name={product.icon.name || "HiCube"}
                        className="w-4 h-4 text-primary"
                        useDefaultColors
                      />
                      {product.shortName}
                    </Link>
                  ))}
                  <Link
                    href="/products/"
                    className="flex items-center gap-3 py-2 px-3 rounded-md text-sm font-bold text-primary hover:bg-muted"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaBars className="w-4 h-4" />
                    View All Products
                  </Link>
                </div>
              </div>

              <div className="px-3 py-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                  Menu
                </p>
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`block py-2 px-3 rounded-md text-sm font-medium transition-colors hover:bg-muted ${
                      normalizedPathname === item.href
                        ? "text-primary bg-primary/10"
                        : "text-foreground"
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>

              <div className="px-3 py-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
                  Resources
                </p>
                <div className="space-y-1">
                  {[
                    // Mirrors desktop Resources dropdown — kept in sync.
                    { href: "/blog/", label: "Blog" },
                    { href: "/alternatives/", label: "Alternatives" },
                    { href: "/comparisons/", label: "Comparisons" },
                    { href: "/best/", label: "Best Tools" },
                    { href: "/tools/", label: "Free Tools" },
                    { href: "/faq/", label: "FAQ" },
                  ].map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={`block py-2 px-3 rounded-md text-sm font-medium transition-colors hover:bg-muted ${
                        normalizedPathname === item.href
                          ? "text-primary bg-primary/10"
                          : "text-foreground"
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            </nav>

            {/* Mobile Action Buttons */}
            <div className="space-y-2 pt-2 pb-4 border-t">
              {actionButtons.map((button, index) => {
                const Component = button.component;
                // Use mobileClassName for mobile menu to ensure buttons are visible
                const mobileClass =
                  ("mobileClassName" in button ? button.mobileClassName : undefined) ||
                  "flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90 transition-colors gap-2";

                if (Component === InstallButton) {
                  const product = getInstallProduct();
                  // Spread props and explicitly set product (product will override any existing product prop)
                  const buttonProps = button.props as Record<string, unknown>;
                  return (
                    <InstallButton
                      key={index}
                      {...buttonProps}
                      className={`${mobileClass} w-full`}
                      product={product}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {button.children}
                    </InstallButton>
                  );
                }
                return (
                  <Link
                    key={index}
                    {...(button.props as React.ComponentProps<typeof Link>)}
                    className={`${mobileClass} w-full`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {button.children}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
